import { clientConfig } from '@/config/clientConfig';
import { useToast } from '@/shared/hooks/useToast';
import { normalizeEmail } from '@/shared/lib/email-utils';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Mail,
} from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuthStore } from '../store/authStore';

type Step = 'request' | 'confirm';

/**
 * Two-step Forgot Password flow.
 *
 *  1. `request` — user enters their email; we ask Cognito to send a
 *     verification code (POST /auth/forgot-password).
 *  2. `confirm` — user pastes the code from the email along with a new
 *     password (POST /auth/confirm-forgot-password).
 *
 * If the page is loaded with `?email=` (e.g. linked to from the toast that
 * fires after an admin reset), we pre-fill the email field so the user only
 * has to paste the verification code.
 */
export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const { requestPasswordReset, confirmPasswordReset, isLoading, error, clearError } =
    useAuthStore();

  const initialEmail = searchParams.get('email') ?? '';
  const startOnConfirm = searchParams.get('step') === 'confirm';

  const [step, setStep] = useState<Step>(startOnConfirm && initialEmail ? 'confirm' : 'request');
  const [email, setEmail] = useState(initialEmail);
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => () => clearError(), [clearError]);

  const validity = useMemo(
    () => ({
      hasLength: newPassword.length >= 12,
      hasUpper: /[A-Z]/.test(newPassword),
      hasLower: /[a-z]/.test(newPassword),
      hasNumber: /\d/.test(newPassword),
      hasSpecial: /[!@#$%^&*_+\-=|<>/?~]/.test(newPassword),
      passwordMatch: confirmPassword.length > 0 ? newPassword === confirmPassword : true,
    }),
    [newPassword, confirmPassword],
  );

  const allValid =
    validity.hasLength &&
    validity.hasUpper &&
    validity.hasLower &&
    validity.hasNumber &&
    validity.hasSpecial &&
    validity.passwordMatch &&
    confirmPassword.length > 0 &&
    verificationCode.trim().length >= 4;

  const handleRequestSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    const normalised = normalizeEmail(email);
    if (!normalised) {
      setValidationError('Email address is required');
      return;
    }

    try {
      await requestPasswordReset(normalised);
      toast({
        title: 'Reset code sent',
        description:
          'If an account exists for that email, a verification code has been sent. Check your inbox.',
        className:
          'bg-green-50 border-green-200 text-green-900 dark:bg-green-900 dark:text-green-100',
      });
      setEmail(normalised);
      setStep('confirm');
    } catch {
      // Cognito will sometimes 4xx if the user is in FORCE_CHANGE_PASSWORD —
      // surface that explicitly so the user can ask their admin to use the
      // "Reset Password" admin action instead, which RESENDs the invite.
      toast({
        title: 'Could not send reset code',
        description:
          error ||
          "We couldn't send a code right now. If this is a new account that has never logged in, ask your administrator to resend the invite.",
        variant: 'destructive',
      });
    }
  };

  const handleConfirmSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!allValid) return;
    setValidationError(null);

    try {
      await confirmPasswordReset({
        username: normalizeEmail(email),
        verificationCode: verificationCode.trim(),
        newPassword,
      });
      toast({
        title: 'Password updated',
        description: 'Your password has been reset. Please sign in with your new password.',
        className:
          'bg-green-50 border-green-200 text-green-900 dark:bg-green-900 dark:text-green-100',
      });
      navigate('/login', { replace: true });
    } catch {
      toast({
        title: 'Could not reset password',
        description: error || 'Please check the verification code and try again.',
        variant: 'destructive',
      });
    }
  };

  const requirements = [
    { key: 'hasLength', label: '12 or more characters', met: validity.hasLength },
    { key: 'hasUpper', label: 'At least 1 uppercase letter', met: validity.hasUpper },
    { key: 'hasLower', label: 'At least 1 lowercase letter', met: validity.hasLower },
    { key: 'hasNumber', label: 'At least 1 numeric character', met: validity.hasNumber },
    { key: 'hasSpecial', label: 'At least 1 special character', met: validity.hasSpecial },
  ];

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url(${clientConfig.loginBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 to-green-900/40" />

      <Card className="w-full max-w-md p-8 space-y-6 bg-white dark:bg-gray-900 shadow-2xl border-border relative z-10">
        <div className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <img src={clientConfig.logo} alt={clientConfig.name} className="h-12 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {step === 'request' ? 'Forgot your password?' : 'Reset your password'}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {step === 'request'
              ? 'Enter the email address associated with your account and we will send you a verification code.'
              : 'Enter the verification code we sent to your email and choose a new password.'}
          </p>
        </div>

        {validationError && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
            {validationError}
          </div>
        )}

        {step === 'request' ? (
          <form onSubmit={handleRequestSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                Email address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                autoComplete="username"
                required
                className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full text-white"
              style={{ backgroundColor: clientConfig.theme.primary }}
              size="lg"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending code…
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send verification code
                </>
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => navigate('/login')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to sign in
            </Button>

            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
              Already have a verification code?{' '}
              <button
                type="button"
                className="underline font-medium"
                onClick={() => {
                  if (!normalizeEmail(email)) {
                    setValidationError('Enter your email first to continue with a code');
                    return;
                  }
                  setStep('confirm');
                }}
              >
                Enter it now
              </button>
              .
            </div>
          </form>
        ) : (
          <form onSubmit={handleConfirmSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label
                htmlFor="confirm-email"
                className="text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                Email address
              </Label>
              <Input
                id="confirm-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                autoComplete="username"
                required
                className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="code"
                className="text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                Verification code
              </Label>
              <Input
                id="code"
                inputMode="numeric"
                placeholder="123456"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                disabled={isLoading}
                autoComplete="one-time-code"
                required
                className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 tracking-widest"
              />
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-1">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password requirements:
              </p>
              {requirements.map((req) => (
                <div key={req.key} className="flex items-center gap-2 text-xs">
                  {req.met ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                  ) : (
                    <Circle className="w-3.5 h-3.5 text-gray-400" />
                  )}
                  <span
                    className={
                      req.met
                        ? 'text-green-700 dark:text-green-400'
                        : 'text-gray-600 dark:text-gray-400'
                    }
                  >
                    {req.label}
                  </span>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="newPassword"
                className="text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                New password
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isLoading}
                  autoComplete="new-password"
                  required
                  className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="confirmPassword"
                className="text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                Confirm new password
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  autoComplete="new-password"
                  required
                  className={`bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 pr-10 ${
                    !validity.passwordMatch ? 'border-red-500' : ''
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {!validity.passwordMatch && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isLoading || !allValid}
              className="w-full text-white"
              style={{ backgroundColor: clientConfig.theme.primary }}
              size="lg"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting password…
                </>
              ) : (
                <>
                  <KeyRound className="w-4 h-4 mr-2" />
                  Set new password
                </>
              )}
            </Button>

            <div className="flex items-center justify-between text-xs">
              <button
                type="button"
                className="text-gray-600 dark:text-gray-400 hover:underline"
                onClick={() => setStep('request')}
              >
                ← Use a different email
              </button>
              <button
                type="button"
                className="text-gray-600 dark:text-gray-400 hover:underline"
                onClick={() => handleRequestSubmit(new Event('submit') as unknown as FormEvent)}
              >
                Resend code
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
